import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { ApiClient } from '@/api/client';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';
import { TransactionsResponseSchema, type TransactionsResponse } from '@/api/schemas/transaction.schema';
import * as React from 'react';
import { logger } from '@/lib/logger'

export function useTransactions(groupId: string) {
  const queryClient = useQueryClient();

  // Listen for offline expense additions and force refetch
  React.useEffect(() => {
    const handleOfflineExpenseAdded = async () => {
      logger.debug('[useTransactions] Offline expense added, invalidating and refetching...');
      // Invalidate and immediately refetch
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS, groupId],
        exact: true
      });
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS, groupId],
        exact: true
      });
    };

    window.addEventListener('offline-expense-added', handleOfflineExpenseAdded);
    return () => {
      window.removeEventListener('offline-expense-added', handleOfflineExpenseAdded);
    };
  }, [queryClient, groupId]);

  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.TRANSACTIONS, groupId],
    queryFn: async ({ pageParam }) => {
      logger.debug('[useTransactions] QueryFn called for group:', { groupId });

      // Always start by getting offline expenses
      const offlineExpensesRaw = localStorage.getItem('offline-expenses');
      logger.debug('[useTransactions] Raw offline expenses from localStorage:', { offlineExpensesRaw });

      const offlineExpenses = JSON.parse(offlineExpensesRaw || '[]');
      logger.debug('[useTransactions] Parsed offline expenses:', { offlineExpenses });

      const groupOfflineExpenses = pageParam ? [] : offlineExpenses
        .filter((e: any) => {
          const matches = e.group_id === groupId;
          logger.debug('[useTransactions] Checking expense:', { id: e.id, group_id: e.group_id, matches });
          return matches;
        })
        .map((e: any) => ({
          ...e,
          type: 'expense' as const,
          // Ensure date is properly formatted
          date: e.created_at || new Date().toISOString(),
          // Keep the actual description
          description: e.description || 'Offline expense',
          category: {
            id: e.category_id,
            group_id: groupId,
            name: 'Pending Sync',
            emoji: '⏳'
          },
          user: {
            id: e.paid_by,
            name: 'You',
            email: 'offline@local'
          },
          // Add visual indicator that it's offline
          is_offline: true,
          synced: false
        }));

      logger.debug(`[useTransactions] Found ${groupOfflineExpenses.length} offline expenses for group ${groupId}`);

      // Try to get data from API
      let apiData: TransactionsResponse | null = null;

      try {
        const program = pipe(
          Effect.gen(function* () {
            const apiClient = yield* ApiClient;
            const url = pageParam
              ? `/groups/${groupId}/settlements/transactions?cursor=${encodeURIComponent(pageParam)}`
              : `/groups/${groupId}/settlements/transactions`;
            const data = yield* apiClient.get(url, TransactionsResponseSchema);
            return data;
          }),
          Effect.provide(MainLayer)
        );
        apiData = await runEffect(program) as TransactionsResponse;
        logger.debug(`[useTransactions] Got ${apiData?.transactions?.length || 0} transactions from API`);
      } catch (error) {
        logger.debug('[useTransactions] Failed to fetch from API, will show offline data only', { error });
        apiData = { transactions: [] };
      }

      // Combine offline and API data
      const allTransactions = [...groupOfflineExpenses, ...(apiData?.transactions || [])];
      logger.debug(`[useTransactions] Returning ${allTransactions.length} total transactions`);

      return {
        ...apiData,
        transactions: allTransactions
      };
    },
    staleTime: 30_000, // 30 seconds
    enabled: !!groupId,
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.hasMore ? lastPage.pagination.nextCursor ?? undefined : undefined,
    initialPageParam: undefined as string | undefined,
    // Keep cached data when offline
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: (failureCount, _error) => {
      // Check if we're actually offline
      const forceOffline = localStorage.getItem('forceOffline') === 'true';
      const isOffline = !navigator.onLine || forceOffline;

      // Don't retry if offline, otherwise use default retry behavior (3 attempts)
      if (isOffline) {
        logger.debug('[useTransactions] Offline, not retrying');
        return false;
      }

      // Retry up to 3 times when online
      return failureCount < 3;
    },
  });
}
